import type { RentalView } from './types';

/** The rentals of one supplier in the event („zamówienie u dostawcy”, owner 2026-10-23). */
export type SupplierGroup = {
  /** Null: the role does not see the supplier (one group for all, without a header name), or suppliers off the list. */
  supplierId: string | null;
  /** Owner 2026-10-24 16:15 (pkt 4): the rentals from suppliers off our list („Spoza listy dostawców”, last). */
  offList?: boolean;
  supplierName: string | null;
  rentals: RentalView[];
  /** Σ costs; null for a role that does not see the cost (rule 7a: sums too). */
  totalGrosze: number | null;
  returned: number;
  /** Owner 2026-11-01: the rows that count (not „Anulowane”) — „N/M zwrócone” is of these. */
  counted: number;
};

/** Owner 2026-11-01: whether a row counts in the sums — not an „Anulowane” one (`counted` comes with the cost). */
export const countsIn = (r: RentalView): boolean => r.counted !== false && r.status !== 'anulowane';

const OFF_KEY = '\u0000off-list';
/** The group of rentals from suppliers off our list. */
export const OFF_GROUP = 'Spoza listy dostawców';

/**
 * Owner 2026-10-23 14:35: „dopożyczenia grupuj najpierw według dostawców i sumuj, a na koniec sumuj całość”. Groups in
 * Polish order of the supplier's name, rows in the order they came.
 */
export function groupsBySupplier(list: readonly RentalView[], seeCost: boolean): { groups: SupplierGroup[]; totalGrosze: number | null } {
  const byKey = new Map<string, SupplierGroup>();
  for (const r of list) {
    const key = r.offList ? OFF_KEY : (r.supplierId ?? '');
    let g = byKey.get(key);
    if (!g) {
      g = r.offList
        ? { supplierId: null, offList: true, supplierName: OFF_GROUP, rentals: [], totalGrosze: seeCost ? 0 : null, returned: 0, counted: 0 }
        : { supplierId: r.supplierId ?? null, supplierName: r.supplierId ? (r.supplierName ?? '') : null, rentals: [], totalGrosze: seeCost ? 0 : null, returned: 0, counted: 0 };
      byKey.set(key, g);
    }
    g.rentals.push(r);
    if (!countsIn(r)) continue;
    g.counted += 1;
    if (g.totalGrosze !== null) g.totalGrosze += r.costGrosze ?? 0;
    if (r.status === 'zwrocone') g.returned += 1;
  }
  const groups = [...byKey.values()].sort((a, b) => Number(!!a.offList) - Number(!!b.offList) || (a.supplierName ?? '').localeCompare(b.supplierName ?? '', 'pl'));
  return { groups, totalGrosze: seeCost ? groups.reduce((n, g) => n + (g.totalGrosze ?? 0), 0) : null };
}

/** The other rentals of `rental`'s supplier in the list, with the versions seen (`group` of the PATCH). */
export function groupRefs(list: readonly RentalView[], rental: RentalView): Array<{ id: string; version: number }> {
  if (!rental.supplierId) return [];
  return list.filter((r) => r.id !== rental.id && r.supplierId === rental.supplierId).map((r) => ({ id: r.id, version: r.version }));
}

/** The group header's combined status. */
export const returnedLabel = (returned: number, count: number): string => (returned === count ? 'wszystko zwrócone' : `${returned}/${count} zwrócone`);

/** For whom a status picked in the chooser is: this row, the selected rows, every row of the row's supplier. */
export type StatusScope = 'one' | 'selected' | 'supplier';

/**
 * The options of the status chooser (owner 2026-10-23 18:46: „dodaj: do zaznaczonych pozycji”). Always „Tylko ta
 * pozycja”; „Do zaznaczonych pozycji (N)” when any row is selected — the clicked row counts in, so a row not selected
 * says „i tej”; „Dla wszystkich pozycji od <dostawca>” when the supplier has more rows. The default: the selected rows
 * when the clicked row is one of them, otherwise this row only.
 */
export function statusScopes(o: { rentalId: string; selected: readonly string[]; supplierName: string | null; supplierOthers: number }): { options: Array<{ scope: StatusScope; label: string }>; default: StatusScope } {
  const inSelection = o.selected.includes(o.rentalId);
  const count = new Set([...o.selected, o.rentalId]).size;
  const options: Array<{ scope: StatusScope; label: string }> = [{ scope: 'one', label: 'Tylko ta pozycja' }];
  if (o.selected.length > 0) options.push({ scope: 'selected', label: `Do zaznaczonych pozycji${inSelection ? '' : ' i tej'} (${count})` });
  if (o.supplierOthers > 0) options.push({ scope: 'supplier', label: `Dla wszystkich pozycji od ${o.supplierName ?? 'tego dostawcy'}` });
  return { options, default: inSelection ? 'selected' : 'one' };
}

/** What „Do zaznaczonych” sends: the selected rows still in the list and the clicked one, with the versions seen. */
export function selectedRefs(list: readonly RentalView[], selected: readonly string[], rentalId: string): Array<{ id: string; version: number }> {
  const ids = new Set([...selected, rentalId]);
  return list.filter((r) => ids.has(r.id)).map((r) => ({ id: r.id, version: r.version }));
}

/** The fields „Zmień dla wszystkich pozycji od <dostawca>” gives to the order (the server's `GROUP_FIELDS`). */
export const GROUP_FIELDS = ['returnPersonId', 'firstDay', 'lastDay', 'status', 'notes'] as const;

/**
 * The `group` the rental form sends: only with the checkbox on, a group field changed and the same supplier — a new
 * supplier takes the rental out of the order (review I3; the server ignores the group then too).
 */
export function formGroup(fields: Record<string, unknown>, forAll: boolean, group: Array<{ id: string; version: number }>, supplierChanged: boolean): Array<{ id: string; version: number }> | undefined {
  if (!forAll || supplierChanged || group.length === 0 || !GROUP_FIELDS.some((k) => k in fields)) return undefined;
  return group;
}
